import { BUILT_IN_ROLES } from '../bootstrap/azure/arm.js';
import { AZURE_INSTANCE_VERSION } from '../bootstrap/azure/instance-template.js';
import { assessAzureInstance } from '../onboarding/azure.js';
import { mayCreateInstance } from './run.js';
/**
 * Where an Azure run would land, judged (IM-49): the checks a preflight adds
 * for Azure in place of AWS's "region set up".
 *
 * Pure, over `AzureRunTargetFacts`, like every other check. Five of them:
 *
 * - **`instance-group`** — the instance's resource group exists, at a
 *   template version this build reads. The installer reads that group before
 *   anything else and fails without it, so its absence is certain to fail:
 *   blocking.
 * - **`instance-location`** — the run goes where the group is. Azure allows a
 *   group in one location and its resources in another, so a mismatch does not
 *   fail the run; it puts the cluster in one place and the state in another,
 *   the IM-91 shape. Advisory.
 * - **`deploy-roles`** — some principal holds the three roles the instance
 *   template gives the deploy app on its group. Advisory, never blocking:
 *   Reader cannot tie the runner's client id to a principal object id (that is
 *   Graph, in the customer's tenant), and a customer may grant the same rights
 *   another way. A missing assignment is worth saying, and not proof.
 * - **`vcpu-quota`** — the location's regional vCPUs, and the families the
 *   installer's own quota check reads, have room for at least one node.
 *   Advisory: the installer computes the exact need from its node pools and
 *   refuses an apply short of it, and this cannot know the pools.
 * - **`region-offers-aks`** — the location offers AKS and the subscription has
 *   registered its provider. Blocking when ARM says no, since the deploy app
 *   cannot register a provider (`ARM_SKIP_PROVIDER_REGISTRATION`) and a cluster
 *   cannot be made where AKS is not offered.
 *
 * Unknown is never pass and never blocking. Under resource-group delegation
 * the quota and the providers are not readable at all, and those two say so
 * rather than ask for more than Reader.
 */
export const AZURE_TARGET_CHECK_IDS = [
    'instance-group',
    'instance-location',
    'deploy-roles',
    'vcpu-quota',
    'region-offers-aks',
];
/** The roles `buildInstanceTemplate` assigns the deploy app on an instance's group. */
const DEPLOY_ROLES = [
    { id: BUILT_IN_ROLES.contributor, name: 'Contributor' },
    { id: BUILT_IN_ROLES.storageBlobDataContributor, name: 'Storage Blob Data Contributor' },
    {
        id: BUILT_IN_ROLES.roleBasedAccessControlAdministrator,
        name: 'Role Based Access Control Administrator',
    },
];
/** The smallest node the installer starts, `Standard_D2pds_v6`: two vCPUs. */
export const SMALLEST_NODE_VCPUS = 2;
/** Families a short count of which stops the first node (`cores` is all of them). */
const GATING_FAMILIES = new Set(['cores', 'StandardDpdsv6Family']);
const sameLocation = (a, b) => a.replaceAll(' ', '').toLowerCase() === b.replaceAll(' ', '').toLowerCase();
const free = (quota) => quota.limit - quota.current;
export const azureTargetChecks = (input) => {
    const checks = [];
    const where = `${input.accountName}, ${input.region}`;
    const creates = mayCreateInstance(input.command);
    const unknown = (id, title, detail, fix) => ({
        id,
        title,
        basis: 'account',
        severity: 'advisory',
        status: 'unknown',
        detail,
        fix,
    });
    // The group: by the instance's id, which is the only way to look it up.
    const group = input.target.instanceGroup;
    const named = input.deploymentId;
    if (named === undefined || named === '') {
        if (creates) {
            checks.push({
                id: 'instance-group',
                title: 'Instance resource group',
                basis: 'account',
                severity: 'blocking',
                status: 'fail',
                detail: 'An Azure instance needs its resource group before its first run, and the group is made for one id',
                fix: `Name the instance in deployment_id, then deploy the instance template for it from ${input.accountName}'s onboarding screen.`,
            });
        }
    }
    else if (group === undefined || group.state !== 'read') {
        checks.push(unknown('instance-group', 'Instance resource group', group === undefined ? `${named}'s resource group was not looked for` : group.reason, `${where}: open the account's onboarding screen to see why the subscription could not be read. The run may still be refused.`));
    }
    else if (group.value === null) {
        checks.push({
            id: 'instance-group',
            title: 'Instance resource group',
            basis: 'account',
            severity: 'blocking',
            status: 'fail',
            detail: `No resource group Qrvey can read carries qrvey-id ${named}`,
            fix: `Deploy the instance template for ${named} at subscription scope, with location ${input.region}, from ${input.accountName}'s onboarding screen. Under resource-group delegation, deploy the delegation template for that group too.`,
        });
    }
    else {
        const found = group.value;
        const assessed = found.version === undefined
            ? undefined
            : assessAzureInstance({ instanceVersion: found.version }, { instanceVersion: AZURE_INSTANCE_VERSION });
        const step = assessed?.steps[0];
        if (assessed === undefined || step === undefined || step.state === 'done') {
            // A group found by its state storage account carries no version of its
            // own (`instanceGroupsOf`); the account is the instance template's, so
            // the group is there.
            checks.push({
                id: 'instance-group',
                title: 'Instance resource group',
                basis: 'account',
                severity: 'blocking',
                status: 'pass',
                detail: `${found.name}, in ${found.location}${step === undefined ? '' : `, ${step.detail}`}`,
            });
        }
        else {
            checks.push({
                id: 'instance-group',
                title: 'Instance resource group',
                basis: 'account',
                severity: assessed.canRun ? 'advisory' : 'blocking',
                status: 'fail',
                detail: `${found.name}: ${step.detail}`,
                fix: `${where}: ${(step.nextAction ?? "see the account's onboarding screen.").replaceAll('from this screen', "from the account's onboarding screen")}`,
            });
        }
        if (creates && found.location !== '' && !sameLocation(found.location, input.region)) {
            checks.push({
                id: 'instance-location',
                title: 'Instance location',
                basis: 'account',
                severity: 'advisory',
                status: 'fail',
                detail: `${found.name} is in ${found.location}, and this run would deploy to ${input.region}`,
                fix: `Run in ${found.location}, where the instance's state is. Azure would accept ${input.region}, and the cluster would then live apart from its state.`,
            });
        }
        else if (creates && found.location !== '') {
            checks.push({
                id: 'instance-location',
                title: 'Instance location',
                basis: 'account',
                severity: 'advisory',
                status: 'pass',
                detail: `The run and the instance's group are both in ${found.location}`,
            });
        }
        const roles = input.target.groupRoles;
        if (roles === undefined || roles.state !== 'read') {
            checks.push(unknown('deploy-roles', 'Deploy app’s roles', roles === undefined ? 'Not read' : roles.reason, `${where}: ask the customer to confirm the deploy app holds Contributor, Storage Blob Data Contributor and Role Based Access Control Administrator on ${found.name}.`));
        }
        else {
            const byPrincipal = new Map();
            for (const assignment of roles.value) {
                const held = byPrincipal.get(assignment.principalId) ?? new Set();
                held.add(assignment.roleDefinition);
                byPrincipal.set(assignment.principalId, held);
            }
            const holder = [...byPrincipal.entries()].find(([, held]) => DEPLOY_ROLES.every((r) => held.has(r.id)));
            checks.push(holder === undefined
                ? {
                    id: 'deploy-roles',
                    title: 'Deploy app’s roles',
                    basis: 'account',
                    severity: 'advisory',
                    status: 'fail',
                    detail: `No principal holds all of ${DEPLOY_ROLES.map((r) => r.name).join(', ')} on ${found.name}`,
                    fix: `${where}: ask the customer to deploy the instance template for ${named} again; it assigns these to the deploy app. If they grant the same rights another way, the run may still work.`,
                }
                : {
                    id: 'deploy-roles',
                    title: 'Deploy app’s roles',
                    basis: 'account',
                    severity: 'advisory',
                    status: 'pass',
                    detail: `Principal ${holder[0]} holds the instance template's three roles on ${found.name}`,
                });
        }
    }
    // The location: only for a run that may create something there.
    if (!creates)
        return checks;
    const quotas = input.target.quotas;
    if (quotas.state !== 'read') {
        checks.push(unknown('vcpu-quota', 'vCPU quota', quotas.reason, `${where}: the installer checks quota itself before it applies and stops if it is short. To know now, ask the customer to run az vm list-usage --location ${input.region}.`));
    }
    else if (quotas.value.length === 0) {
        checks.push(unknown('vcpu-quota', 'vCPU quota', `Azure reported no vCPU quota for ${input.region}`, `${where}: check that ${input.region} is a location the subscription can use. The installer checks quota itself before it applies.`));
    }
    else {
        const short = quotas.value.filter((q) => GATING_FAMILIES.has(q.name) && free(q) < SMALLEST_NODE_VCPUS);
        const summary = quotas.value
            .map((q) => `${q.name} ${String(free(q))} of ${String(q.limit)} free`)
            .join('; ');
        checks.push(short.length === 0
            ? {
                id: 'vcpu-quota',
                title: 'vCPU quota',
                basis: 'account',
                severity: 'advisory',
                status: 'pass',
                detail: `${summary}. The installer checks the exact need from its node pools before it applies`,
            }
            : {
                id: 'vcpu-quota',
                title: 'vCPU quota',
                basis: 'account',
                severity: 'advisory',
                status: 'fail',
                detail: `${summary}: not enough for one ${String(SMALLEST_NODE_VCPUS)}-vCPU node`,
                fix: `${where}: ask the customer to raise ${short.map((q) => q.name).join(' and ')} in ${input.region} (Azure portal, Quotas). The installer will refuse the apply until there is room.`,
            });
    }
    const aks = input.target.aks;
    if (aks.state !== 'read') {
        checks.push(unknown('region-offers-aks', 'AKS in this location', aks.reason, `${where}: check that ${input.region} offers AKS and that Microsoft.ContainerService is registered in the subscription.`));
    }
    else if (aks.value.offered) {
        checks.push({
            id: 'region-offers-aks',
            title: 'AKS in this location',
            basis: 'account',
            severity: 'blocking',
            status: 'pass',
            detail: `${input.region} offers Kubernetes ${aks.value.versions.slice(-1)[0] ?? ''}`.trim(),
        });
    }
    else {
        const unregistered = aks.value.code === 'NoRegisteredProviderFound' ||
            aks.value.code === 'MissingSubscriptionRegistration';
        checks.push({
            id: 'region-offers-aks',
            title: 'AKS in this location',
            basis: 'account',
            severity: 'blocking',
            status: 'fail',
            detail: unregistered
                ? 'Microsoft.ContainerService is not registered in this subscription'
                : `${input.region} does not offer AKS to this subscription (${aks.value.code})`,
            fix: unregistered
                ? `${input.accountName}: ask the customer to register Microsoft.ContainerService (az provider register --namespace Microsoft.ContainerService). The deploy app cannot register providers.`
                : `Choose a location that offers AKS.`,
        });
    }
    return checks;
};
//# sourceMappingURL=preflight-azure.js.map